import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";

import { Button } from "../../../ui/Button/Button";
import { StatusBar } from "../../../ui/StatusBar/StatusBar";
import { CompareIcon } from "../../../ui/icons/CompareIcon";
import {
  FiltersSidebar,
  useFiltersState,
} from "../../FiltersSidebar/FiltersSidebar";
import {
  TiresCompareDialog,
  compareTiresHash,
} from "../TiresCompareDialog/TireCompareDialog";
import { useTireCompareIds } from "../TiresCompareDialog/useTireCompareIds";
import { TireFilters } from "./Filters/TireFilters";
import { TireQuickSeasonFilters } from "./Filters/TireSeasonFilters/TireQuickSeasonFilters";
import { PackageResultsLink } from "./PackageResultsLink";
import { TireSearchResults, useTireResults } from "./useTireResults";

type TireStatusbarProps = {
  className?: string;
};

const hasPricingIssues = (results?: TireSearchResults | null): boolean =>
  !!results?.allParts
    .slice(0, 3)
    .some(({ partPricing }) =>
      Object.values(partPricing.primaryPart.price || {})[0]?.contextList.some(
        ({ displayPrice }) => !Number(displayPrice),
      ),
    );

export const TireStatusbar: FunctionComponent<TireStatusbarProps> = ({
  className,
}) => {
  const { t } = useTranslation();
  const [, { hide }] = useFiltersState();
  const [compareIds] = useTireCompareIds();
  const { data, isLoading } = useTireResults();

  return (
    <>
      <StatusBar
        className={clsx("mb-4 md:mb-6 lg:mb-9 flex-wrap gap-1", className)}
        isLoading={isLoading}
      >
        <>
          <Link href={compareTiresHash}>
            <Button
              aria-label="openTireCompareDialog"
              className="hidden md:flex"
              disabled={!data || hasPricingIssues(data)}
              element="a"
            >
              <CompareIcon />
              {t(compareIds.length ? "compare.selected" : "compare.quick")}
            </Button>
          </Link>
          <PackageResultsLink className="order-3 sm:order-none w-full sm:w-auto justify-center sm:justify-start" />
        </>

        <>
          <FiltersSidebar>
            <TireFilters onClose={hide} />
          </FiltersSidebar>
          <TireQuickSeasonFilters />
        </>
      </StatusBar>
      <TiresCompareDialog />
    </>
  );
};
